import React, { useState } from 'react';

import { Settings, Visibility } from '@mui/icons-material';
import { Button, Checkbox, FormControlLabel, Popover, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

interface ColumnOption {
  field: string;
  label: string;
  defaultVisible?: boolean;
}

interface ColumnSelectorProps<T = any> {
  columns: GridColDef<T>[];
  columnVisibilityModel: Record<string, boolean>;
  onColumnVisibilityChange: (field: string, visible: boolean) => void;
  size?: 'small' | 'medium' | 'large';
}

export default function ColumnSelector<T = any>({
  columns,
  columnVisibilityModel,
  onColumnVisibilityChange,
  size = 'small',
}: ColumnSelectorProps<T>) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Generate column options from actual columns
  const columnOptions: ColumnOption[] = columns
    .filter((col) => col.headerName && col.headerName !== '') // Only show columns with visible headers
    .map((col) => ({
      field: col.field,
      label: col.headerName as string,
      defaultVisible: columnVisibilityModel[col.field] ?? true,
    }));

  // Function to show all columns
  const handleShowAllColumns = () => {
    const allColumnsVisible = columnOptions.reduce((acc, column) => {
      acc[column.field] = true;
      return acc;
    }, {} as Record<string, boolean>);

    // Update all columns to be visible
    Object.entries(allColumnsVisible).forEach(([field, visible]) => {
      onColumnVisibilityChange(field, visible);
    });
  };

  return (
    <>
      <Button
        size={size}
        variant="outlined"
        onClick={handleClick}
        startIcon={<Settings />}
        title="Column visibility"
        sx={{ minWidth: 'auto', px: 1 }}
      >
        Columns
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Stack spacing={1} sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}>
            Show/Hide Columns
          </Typography>

          {/* Show All Columns Button */}
          <Button
            size="small"
            variant="outlined"
            onClick={handleShowAllColumns}
            startIcon={<Visibility />}
            sx={{ mb: 1 }}
            fullWidth
          >
            Show All Columns
          </Button>

          {columnOptions.map((column) => (
            <FormControlLabel
              key={column.field}
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibilityModel[column.field] ?? column.defaultVisible ?? true}
                  onChange={(e) => onColumnVisibilityChange(column.field, e.target.checked)}
                />
              }
              label={column.label}
              sx={{ m: 0 }}
            />
          ))}
        </Stack>
      </Popover>
    </>
  );
}
